// DocumentLetterhead.jsx

import React from 'react';

// Context
import { useBase } from 'context/BaseContext';

// Services
import { getDocumentLetterheadSettings } from 'services/bases/documentBaseSettings';

// Utilities
import { PhotoShape } from 'utilities/Enums';
import { openBaseSettings } from 'utilities/openBaseSettings';
import { formatDocumentAddress } from 'nodetypes/documents/kit/documentAddress';

// CSS
import 'nodetypes/purchaseorder/view/PurchaseOrderGeneralInfo.css';

// Components
import BaseCover from 'components/basecover/BaseCover';

/**
 * DocumentLetterhead
 *
 * Shared company block (icon, name, phone, address) for business documents.
 *
 * @param {object|null} [letterheadOverride] - Portal/static letterhead payload.
 */
const DocumentLetterhead = React.memo(({
  letterheadOverride = null,
}) => {
  // ----- CONTEXT ----- //

  const { activeBase } = useBase();

  // ----- MEMOS ----- //

  const isPortalLetterhead = Boolean(letterheadOverride);
  const settings = getDocumentLetterheadSettings(activeBase);

  const letterhead = letterheadOverride && typeof letterheadOverride === 'object'
    ? {
      title: String(letterheadOverride.title || '').trim() || 'Company',
      phone: String(letterheadOverride.phone || '').trim(),
      address: formatDocumentAddress(letterheadOverride.address)
        || String(letterheadOverride.addressText || '').trim(),
      iconUrl: String(letterheadOverride.iconUrl || '').trim(),
    }
    : {
      title: String(activeBase?.title || '').trim() || 'Base',
      phone: settings.phone,
      address: formatDocumentAddress(settings.address),
      iconUrl: '',
    };

  // ----- RENDER ----- //

  return (
    <div className="po-general-info-company">
      {isPortalLetterhead ? (
        <>
          <div className="po-general-info-icon-static" aria-hidden="true">
            <BaseCover
              base={{
                title: letterhead.title,
                iconPhotoUrl: letterhead.iconUrl || '',
              }}
              size={80}
              shape={PhotoShape.CIRCLE}
              assetType="icon"
              borderWidth={0}
            />
          </div>
          <div className="po-general-info-company-text">
            <div className="po-general-info-company-title">{letterhead.title}</div>
            {letterhead.phone ? (
              <div className="po-general-info-letterhead-static">{letterhead.phone}</div>
            ) : null}
            {letterhead.address ? (
              <div className="po-general-info-letterhead-static">{letterhead.address}</div>
            ) : null}
          </div>
        </>
      ) : (
        <>
          <button
            type="button"
            className="po-general-info-icon-button"
            title="Edit base icon in settings"
            onClick={() => openBaseSettings('about')}
          >
            <BaseCover
              base={activeBase}
              size={80}
              shape={PhotoShape.CIRCLE}
              assetType="icon"
              borderWidth={0}
            />
          </button>
          <div className="po-general-info-company-text">
            <div className="po-general-info-company-title">{letterhead.title}</div>
            {letterhead.phone ? (
              <button
                type="button"
                className="po-general-info-letterhead-link"
                title="Edit phone in Documents settings"
                onClick={() => openBaseSettings('documents')}
              >
                {letterhead.phone}
              </button>
            ) : null}
            {letterhead.address ? (
              <button
                type="button"
                className="po-general-info-letterhead-link"
                title="Edit address in Documents settings"
                onClick={() => openBaseSettings('documents')}
              >
                {letterhead.address}
              </button>
            ) : (
              <button
                type="button"
                className="po-general-info-letterhead-link is-empty"
                title="Add phone & address in Documents settings"
                onClick={() => openBaseSettings('documents')}
              >
                Add phone &amp; address in Base settings → Documents
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
});

DocumentLetterhead.displayName = 'DocumentLetterhead';

export default DocumentLetterhead;
